// Player Card Menu Component
// Handles the interactive menu that appears when clicking another player's card

let currentOpenMenu = null;
let menuOverlay = null;
let isMenuOpen = false;

// Emojis for reactions
const REACTIONS = {
  laugh: '😂',
  hundred: '💯',
  poop: '💩',
  thinking: '🤔'
};

// Track nudge cooldowns per target player (targetPlayerId -> timestamp)
// Server is source of truth - these are set based on server responses
const nudgeCooldowns = {};
const NUDGE_COOLDOWN_MS = 5000; // 5 seconds

/**
 * Initialize player card menu system
 * @param {Object} socket - Socket.io client instance
 * @param {Function} getCurrentSessionId - Function to get current player's session ID
 * @param {Function} getGameState - Function to get current game state (cardsFlipped, players)
 */
export function initializePlayerCardMenu(socket, getCurrentSessionId = null, getGameState = null) {
  // Use event delegation on the player cards container
  const playerCardsContainer = document.getElementById('player-cards');

  if (playerCardsContainer) {
    // Add click listener with event delegation
    playerCardsContainer.addEventListener('click', (event) => {
      // Only handle if not currently in the process of opening/closing
      if (isMenuOpen) return;

      handlePlayerCardClick(event, socket, getCurrentSessionId, getGameState);
    }, true); // Use capture phase
  }
}

/**
 * Handle clicks on player cards
 */
function handlePlayerCardClick(event, socket, getCurrentSessionId, getGameState) {
  // Don't open menu if clicking inside an already open menu
  if (event.target.closest('.player-card-menu')) {
    return;
  }

  // Find the closest box-item (player card)
  const playerCard = event.target.closest('.box-item');

  if (!playerCard) return;

  // Get the player ID from the card (this is now a session ID)
  const playerId = playerCard.id.replace('player-', '');

  // Don't show menu for own card (compare session IDs)
  const mySessionId = getCurrentSessionId ? getCurrentSessionId() : null;
  if (mySessionId && playerId === mySessionId) {
    return;
  }

  // Stop event from doing other things
  event.stopPropagation();
  event.preventDefault();

  // Close any existing menu first
  if (currentOpenMenu) {
    closeMenu();
    // Wait a bit before opening new menu
    setTimeout(() => {
      openMenu(playerCard, playerId, socket, getGameState);
    }, 100);
  } else {
    openMenu(playerCard, playerId, socket, getGameState);
  }
}

/**
 * Open the menu for a player card
 */
function openMenu(playerCard, playerId, socket, getGameState) {
  isMenuOpen = true;

  // Get card position
  const rect = playerCard.getBoundingClientRect();

  // Create overlay
  menuOverlay = document.createElement('div');
  menuOverlay.className = 'player-card-menu-overlay';
  document.body.appendChild(menuOverlay);

  // Create menu (attached to body, not card)
  const menu = document.createElement('div');
  menu.className = 'player-card-menu';
  menu.dataset.playerId = playerId;

  // Position menu above the card using fixed positioning
  menu.style.position = 'fixed';
  menu.style.left = `${rect.left + rect.width / 2}px`;
  menu.style.top = `${rect.top - 10}px`; // 10px above card
  menu.style.transform = 'translateX(-50%) translateY(-100%)';

  // Prevent clicks on menu from closing it
  menu.onclick = (e) => {
    e.stopPropagation();
  };

  // Add emoji reaction buttons
  Object.entries(REACTIONS).forEach(([key, emoji]) => {
    const btn = document.createElement('button');
    btn.className = 'emoji-reaction-btn';
    btn.textContent = emoji;
    btn.dataset.reaction = key;
    btn.onclick = () => handleReaction(playerId, key, emoji, playerCard, socket);
    menu.appendChild(btn);
  });

  // Add separator
  const separator = document.createElement('div');
  separator.style.width = '2px';
  separator.style.height = '24px';
  separator.style.backgroundColor = 'var(--border-color)';
  separator.style.margin = '0 4px';
  menu.appendChild(separator);

  // Add nudge button (only show when cards not flipped and target hasn't voted)
  const gameState = getGameState ? getGameState() : null;
  const canNudge = gameState && !gameState.cardsFlipped && gameState.players;

  if (canNudge) {
    const targetPlayer = gameState.players.find(p => p.id === playerId);
    const hasVoted = targetPlayer && targetPlayer.cardSelected;

    const nudgeBtn = document.createElement('button');
    nudgeBtn.className = 'nudge-player-btn';
    nudgeBtn.dataset.targetId = playerId; // Store target ID for countdown updates

    // Check if there's an active cooldown for this target
    const cooldownTimestamp = nudgeCooldowns[playerId];
    const now = Date.now();
    const remainingCooldown = cooldownTimestamp ? Math.max(0, NUDGE_COOLDOWN_MS - (now - cooldownTimestamp)) : 0;

    if (hasVoted) {
      // Disable if player already voted
      nudgeBtn.textContent = 'Nudge';
      nudgeBtn.disabled = true;
    } else if (remainingCooldown > 0) {
      // In cooldown - show countdown
      const secondsLeft = Math.ceil(remainingCooldown / 1000);
      nudgeBtn.textContent = `${secondsLeft}s`;
      nudgeBtn.disabled = true;
      nudgeBtn.classList.add('cooldown');

      // Start countdown timer
      startNudgeCooldownTimer(nudgeBtn, playerId, remainingCooldown);
    } else {
      // Ready to nudge
      nudgeBtn.textContent = 'Nudge';
      nudgeBtn.disabled = false;
    }

    nudgeBtn.onclick = () => handleNudge(playerId, socket);
    menu.appendChild(nudgeBtn);
  }

  // Add kick button
  const kickBtn = document.createElement('button');
  kickBtn.className = 'kick-player-btn';
  kickBtn.textContent = 'Kick';
  kickBtn.onclick = () => handleKickVote(playerId, socket);
  menu.appendChild(kickBtn);

  // Add menu to body (not to card)
  document.body.appendChild(menu);
  currentOpenMenu = menu;

  // Close menu when clicking overlay
  setTimeout(() => {
    if (menuOverlay) {
      menuOverlay.onclick = () => closeMenu();
    }
  }, 100);
}

/**
 * Close the currently open menu
 */
function closeMenu() {
  if (currentOpenMenu) {
    // Clean up any active countdown intervals
    const nudgeBtn = currentOpenMenu.querySelector('.nudge-player-btn');
    if (nudgeBtn && nudgeBtn.dataset.intervalId) {
      clearInterval(parseInt(nudgeBtn.dataset.intervalId));
    }

    // Add closing animation
    currentOpenMenu.classList.add('closing');

    // Remove after animation
    setTimeout(() => {
      if (currentOpenMenu && currentOpenMenu.parentNode) {
        currentOpenMenu.parentNode.removeChild(currentOpenMenu);
      }
      currentOpenMenu = null;
      isMenuOpen = false;
    }, 300);
  } else {
    isMenuOpen = false;
  }

  // Remove overlay
  if (menuOverlay) {
    if (menuOverlay.parentNode) {
      menuOverlay.parentNode.removeChild(menuOverlay);
    }
    menuOverlay = null;
  }
}

/**
 * Handle emoji reaction
 */
function handleReaction(playerId, reactionKey, emoji, playerCard, socket) {
  // Emit reaction to server - server will broadcast to everyone including sender
  socket.emit('playerReaction', {
    targetPlayerId: playerId,
    reaction: reactionKey,
    emoji: emoji
  });

  // Don't create local animation - wait for server broadcast to avoid double emoji
}

/**
 * Create floating emoji animation
 */
export function createFloatingEmoji(emoji, playerCard) {
  const floatingEmoji = document.createElement('div');
  floatingEmoji.className = 'floating-emoji';
  floatingEmoji.textContent = emoji;

  // Get card position
  const rect = playerCard.getBoundingClientRect();

  // Random horizontal offset (-30 to 30 pixels)
  const randomX = (Math.random() - 0.5) * 60;

  // Position at the card
  floatingEmoji.style.left = `${rect.left + rect.width / 2}px`;
  floatingEmoji.style.top = `${rect.top + rect.height / 2}px`;
  floatingEmoji.style.setProperty('--random-x', `${randomX}px`);

  document.body.appendChild(floatingEmoji);

  // Remove after animation
  setTimeout(() => {
    if (floatingEmoji.parentNode) {
      floatingEmoji.parentNode.removeChild(floatingEmoji);
    }
  }, 2000);
}

/**
 * Start countdown timer for nudge button
 */
function startNudgeCooldownTimer(nudgeBtn, playerId, remainingMs) {
  const startTime = Date.now();
  const endTime = startTime + remainingMs;

  const updateCountdown = () => {
    const now = Date.now();
    const timeLeft = Math.max(0, endTime - now);
    const secondsLeft = Math.ceil(timeLeft / 1000);

    if (secondsLeft > 0) {
      nudgeBtn.textContent = `${secondsLeft}s`;
    } else {
      // Cooldown finished
      nudgeBtn.textContent = 'Nudge';
      nudgeBtn.disabled = false;
      nudgeBtn.classList.remove('cooldown');
    }
  };

  // Update immediately
  updateCountdown();

  // Update every 100ms for smooth countdown
  const interval = setInterval(() => {
    updateCountdown();

    // Stop when countdown is done
    if (Date.now() >= endTime) {
      clearInterval(interval);
    }
  }, 100);

  // Store interval ID on button so we can clean it up if menu closes
  nudgeBtn.dataset.intervalId = interval;
}

/**
 * Handle nudge action
 */
function handleNudge(playerId, socket) {
  // Close menu
  closeMenu();

  // Emit nudge to server - server will validate and respond
  socket.emit('nudgePlayer', {
    targetPlayerId: playerId
  });
}

/**
 * Handle kick vote initiation
 */
function handleKickVote(playerId, socket) {
  // Close menu
  closeMenu();

  // Emit kick vote to server
  socket.emit('initiateKickVote', {
    targetPlayerId: playerId
  });
}

/**
 * Clear all nudge cooldowns (called on game reset)
 */
export function clearNudgeCooldowns() {
  for (const key in nudgeCooldowns) {
    delete nudgeCooldowns[key];
  }
}

/**
 * Handle nudge failure from server (e.g., cooldown active)
 * This is called from index.js when server sends nudgeFailed event
 */
export function handleNudgeFailed({ targetPlayerId, remainingMs }) {
  // Record the cooldown timestamp based on server's remaining time
  nudgeCooldowns[targetPlayerId] = Date.now() - (NUDGE_COOLDOWN_MS - remainingMs);
}

/**
 * Handle successful nudge from server
 * This is called from index.js when server sends playerNudged event
 */
export function handleNudgeSuccess({ targetPlayerId, nudgerSessionId }, currentSessionId) {
  // If we're the nudger, record the cooldown
  if (nudgerSessionId === currentSessionId) {
    nudgeCooldowns[targetPlayerId] = Date.now();
  }
}

/**
 * Sync nudge cooldowns from server (called on join/refresh)
 * This is called from index.js when server sends nudgeCooldownsSync event
 */
export function syncNudgeCooldowns({ cooldowns }) {
  // Apply all cooldowns from server
  cooldowns.forEach(({ targetPlayerId, remainingMs }) => {
    // Calculate the timestamp based on remaining time
    nudgeCooldowns[targetPlayerId] = Date.now() - (NUDGE_COOLDOWN_MS - remainingMs);
  });
  console.log(`Synced ${cooldowns.length} nudge cooldowns from server`);
}

/**
 * Clean up when room changes
 */
export function cleanupPlayerCardMenu() {
  if (currentOpenMenu) {
    closeMenu();
  }
}
